<template>
  <div class="maintain-page">
    <a-spin :spinning="spinning">
      <div class="page-hd">
        <div class="page-title">检修状态配置</div>
        <div class="tip">批量粘贴支持换行 / 逗号 / 分号 / 空格；IMEI、设备编号、厂编之间为「或」关系。IMEI 同时匹配设备表 name 与蓝牙表 imei。</div>
      </div>

      <a-card size="small" :bordered="false" class="search-card">
        <div class="toolbar">
          <div class="toolbar-left">
            <a-select
              v-model="form.status"
              allowClear
              placeholder="全部状态"
              class="w-status"
            >
              <a-select-option :value="0">在厂</a-select-option>
              <a-select-option :value="1">待测试</a-select-option>
              <a-select-option :value="2">测试完成</a-select-option>
              <a-select-option :value="3">故障</a-select-option>
              <a-select-option :value="4">出厂</a-select-option>
              <a-select-option :value="5">异常</a-select-option>
            </a-select>
            <a-input
              v-model="form.nameORnumber"
              allowClear
              placeholder="关键字：编号 / IMEI / 蓝牙IMEI / 客户"
              class="w-keyword"
              @pressEnter="onSearch"
            />
            <a-button type="primary" class="search-btn" @click="onSearch">查询</a-button>
            <a-button @click="onReset">重置</a-button>
            <a @click="batchOpen = !batchOpen" class="batch-toggle">
              {{ batchOpen ? '收起批量条件' : '展开批量条件' }}
              <a-icon :type="batchOpen ? 'up' : 'down'" />
            </a>
          </div>
          <div class="toolbar-right">
            <a-button
              type="danger"
              :disabled="!selectedRowKeys.length"
              @click="openBatchModal"
            >批量修改状态（已选 {{ selectedRowKeys.length }}）</a-button>
          </div>
        </div>

        <div v-show="batchOpen" class="batch-panel">
          <a-row :gutter="12">
            <a-col :xs="24" :sm="8">
              <div class="field-label">IMEI（设备 name / 蓝牙 imei）</div>
              <a-textarea
                v-model="form.nameText"
                :rows="3"
                placeholder="多个 IMEI，每行一个或用逗号分隔"
                allow-clear
              />
            </a-col>
            <a-col :xs="24" :sm="8">
              <div class="field-label">设备编号（vptNum）</div>
              <a-textarea
                v-model="form.vptNumText"
                :rows="3"
                placeholder="多个设备编号"
                allow-clear
              />
            </a-col>
            <a-col :xs="24" :sm="8">
              <div class="field-label">厂编（deviceNum）</div>
              <a-textarea
                v-model="form.deviceNumText"
                :rows="3"
                placeholder="多个厂编"
                allow-clear
              />
            </a-col>
          </a-row>
        </div>
      </a-card>

      <a-card size="small" :bordered="false" class="table-card">
        <a-table
          class="maintain-table"
          size="small"
          rowKey="objectid"
          :loading="loading"
          :columns="columns"
          :data-source="list"
          :pagination="pagination"
          :row-selection="rowSelection"
          :scroll="{ x: 1180 }"
          @change="handleTableChange"
        >
          <template slot="cellFull" slot-scope="text">
            <span class="cell-full">{{ text || '-' }}</span>
          </template>
          <template slot="status" slot-scope="text">
            <a-tag :color="statusColor(text)">{{ statusLabel(text) }}</a-tag>
          </template>
          <template slot="deviceType" slot-scope="text">
            {{ typeLabel(text) }}
          </template>
          <template slot="customer" slot-scope="text">
            <a-tooltip v-if="text" placement="topLeft" :title="text">
              <span class="cell-ellipsis">{{ text }}</span>
            </a-tooltip>
            <span v-else>-</span>
          </template>
          <template slot="pos" slot-scope="text">
            <a-tooltip v-if="text" placement="topLeft" :title="text" :overlayStyle="{ maxWidth: '480px' }">
              <span class="cell-ellipsis pos-text">{{ text }}</span>
            </a-tooltip>
            <span v-else>-</span>
          </template>
          <template slot="operation" slot-scope="text, record">
            <a href="javascript:;" class="op-link" @click="openSingleModal(record)">改状态</a>
            <a href="javascript:;" class="op-link" @click="openRecordModal(record)">记录</a>
          </template>
        </a-table>
      </a-card>
    </a-spin>

    <a-modal
      :title="batchMode ? ('批量修改状态（' + selectedRowKeys.length + ' 台）') : '修改状态'"
      :visible="modalVisible"
      :confirmLoading="saving"
      okText="确认修改"
      cancelText="取消"
      @ok="submitStatus"
      @cancel="modalVisible = false"
    >
      <a-form-model layout="vertical">
        <a-form-model-item label="目标状态" required>
          <a-radio-group v-model="editForm.status">
            <a-radio :value="0">在厂</a-radio>
            <a-radio :value="1">待测试</a-radio>
            <a-radio :value="2">测试完成</a-radio>
            <a-radio :value="3">故障</a-radio>
            <a-radio :value="4">出厂</a-radio>
            <a-radio :value="5">异常</a-radio>
          </a-radio-group>
        </a-form-model-item>
        <template v-if="editForm.status === 4">
          <a-form-model-item label="所属客户" required>
            <a-input v-model="editForm.customer" placeholder="出厂必填" />
          </a-form-model-item>
          <a-form-model-item label="客户联系人">
            <a-input v-model="editForm.customerContact" placeholder="选填" />
          </a-form-model-item>
          <a-form-model-item label="客户备注">
            <a-input v-model="editForm.customerRemark" placeholder="选填" />
          </a-form-model-item>
        </template>
        <template v-if="editForm.status === 3">
          <a-form-model-item label="故障原因" required>
            <a-textarea
              v-model="editForm.faultReason"
              :rows="3"
              placeholder="请描述故障现象或原因"
              :maxLength="500"
            />
          </a-form-model-item>
        </template>
        <template v-if="editForm.status === 5">
          <a-form-model-item label="异常信息" required>
            <a-textarea
              v-model="editForm.customerRemark"
              :rows="3"
              placeholder="请描述异常情况"
              :maxLength="255"
            />
          </a-form-model-item>
        </template>
      </a-form-model>
    </a-modal>

    <a-modal
      :title="recordTitle"
      :visible="recordVisible"
      :footer="null"
      width="860px"
      @cancel="recordVisible = false"
    >
      <a-table
        size="small"
        rowKey="id"
        :loading="recordLoading"
        :columns="recordColumns"
        :data-source="recordList"
        :pagination="recordPagination"
        @change="handleRecordTableChange"
      >
        <template slot="oldStatus" slot-scope="text">
          <a-tag :color="statusColor(text)">{{ statusLabel(text) }}</a-tag>
        </template>
        <template slot="newStatus" slot-scope="text">
          <a-tag :color="statusColor(text)">{{ statusLabel(text) }}</a-tag>
        </template>
        <template slot="emptyText" slot-scope="text">
          {{ text || '-' }}
        </template>
      </a-table>
    </a-modal>
  </div>
</template>

<script>
import {
  findMaintenancePage,
  updateMaintainStatus,
  batchUpdateMaintainStatus,
  findStatusRecordPage
} from '@/api/device'

const STATUS_MAP = {
  0: '在厂',
  1: '待测试',
  2: '测试完成',
  3: '故障',
  4: '出厂',
  5: '异常'
}
const STATUS_COLOR = {
  0: 'blue',
  1: 'orange',
  2: 'green',
  3: 'red',
  4: 'purple',
  5: 'gold'
}

export default {
  name: 'MaintenanceStatus',
  data() {
    return {
      spinning: false,
      loading: false,
      saving: false,
      batchOpen: false,
      form: {
        nameText: '',
        vptNumText: '',
        deviceNumText: '',
        nameORnumber: '',
        status: undefined
      },
      list: [],
      selectedRowKeys: [],
      selectedRows: [],
      pagination: {
        current: 1,
        pageSize: 50,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: ['20', '50', '100', '200', '500'],
        showTotal: (t) => `共 ${t} 条`
      },
      columns: [
        { title: '设备编号', dataIndex: 'vptNum', key: 'vptNum', width: 88, scopedSlots: { customRender: 'cellFull' } },
        { title: '设备IMEI', dataIndex: 'name', key: 'name', width: 132, scopedSlots: { customRender: 'cellFull' } },
        { title: '蓝牙IMEI', dataIndex: 'imei', key: 'imei', width: 132, scopedSlots: { customRender: 'cellFull' } },
        { title: '厂编', dataIndex: 'deviceNum', key: 'deviceNum', width: 78, scopedSlots: { customRender: 'cellFull' } },
        { title: '站点编码', dataIndex: 'vptCode', key: 'vptCode', width: 96, scopedSlots: { customRender: 'cellFull' } },
        { title: '状态', dataIndex: 'status', key: 'status', width: 78, scopedSlots: { customRender: 'status' } },
        { title: '类型', dataIndex: 'deviceType', key: 'deviceType', width: 64, scopedSlots: { customRender: 'deviceType' } },
        { title: '客户', dataIndex: 'customer', key: 'customer', width: 90, scopedSlots: { customRender: 'customer' } },
        { title: '位置', dataIndex: 'pos', key: 'pos', width: 260, scopedSlots: { customRender: 'pos' } },
        { title: '操作', key: 'operation', width: 118, fixed: 'right', scopedSlots: { customRender: 'operation' } }
      ],
      modalVisible: false,
      batchMode: true,
      currentRecord: null,
      editForm: {
        status: 0,
        customer: '',
        customerContact: '',
        customerRemark: '',
        faultReason: ''
      },
      recordVisible: false,
      recordLoading: false,
      recordDevice: null,
      recordList: [],
      recordPagination: {
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50'],
        showTotal: (t) => `共 ${t} 条`
      },
      recordColumns: [
        { title: '变更时间', dataIndex: 'changeTime', key: 'changeTime', width: 160 },
        { title: '变更前', dataIndex: 'oldStatus', key: 'oldStatus', width: 90, scopedSlots: { customRender: 'oldStatus' } },
        { title: '变更后', dataIndex: 'newStatus', key: 'newStatus', width: 90, scopedSlots: { customRender: 'newStatus' } },
        { title: '客户', dataIndex: 'customer', key: 'customer', width: 120, scopedSlots: { customRender: 'emptyText' } },
        { title: '操作人', dataIndex: 'operatorName', key: 'operatorName', width: 100, scopedSlots: { customRender: 'emptyText' } },
        { title: '备注', dataIndex: 'remark', key: 'remark', ellipsis: true, scopedSlots: { customRender: 'emptyText' } }
      ]
    }
  },
  computed: {
    rowSelection() {
      return {
        selectedRowKeys: this.selectedRowKeys,
        onChange: (keys, rows) => {
          this.selectedRowKeys = keys
          this.selectedRows = rows
        },
        columnWidth: 40
      }
    },
    recordTitle() {
      if (!this.recordDevice) return '状态调整记录'
      const no = this.recordDevice.vptNum || this.recordDevice.name || ''
      return no ? `状态调整记录 - ${no}` : '状态调整记录'
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    statusLabel(v) {
      if (v == null) return STATUS_MAP[0]
      return STATUS_MAP[v] || '未知'
    },
    statusColor(v) {
      const key = v == null ? 0 : v
      return STATUS_COLOR[key] || 'default'
    },
    typeLabel(v) {
      if (v === 1) return 'RTU'
      if (v === 2) return '电子锁'
      return '未设'
    },
    splitText(text) {
      if (!text) return []
      return String(text)
        .split(/[\s,;，；\n\r\t]+/)
        .map(s => s.trim())
        .filter(Boolean)
    },
    buildQuery() {
      return {
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
        status: this.form.status,
        nameORnumber: (this.form.nameORnumber || '').trim(),
        nameList: this.splitText(this.form.nameText),
        vptNumList: this.splitText(this.form.vptNumText),
        deviceNumList: this.splitText(this.form.deviceNumText)
      }
    },
    onSearch() {
      this.pagination.current = 1
      this.selectedRowKeys = []
      this.selectedRows = []
      this.loadData()
    },
    onReset() {
      this.form = {
        nameText: '',
        vptNumText: '',
        deviceNumText: '',
        nameORnumber: '',
        status: undefined
      }
      this.onSearch()
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.loadData()
    },
    loadData() {
      this.loading = true
      findMaintenancePage(this.buildQuery())
        .then(res => {
          const data = (res && res.data) || {}
          this.list = data.rows || []
          this.pagination.total = data.total || 0
        })
        .catch(() => {
          this.$message.error('查询失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    openBatchModal() {
      if (!this.selectedRowKeys.length) {
        this.$message.warning('请先勾选设备')
        return
      }
      this.batchMode = true
      this.currentRecord = null
      this.editForm = { status: 0, customer: '', customerContact: '', customerRemark: '', faultReason: '' }
      this.modalVisible = true
    },
    openSingleModal(record) {
      this.batchMode = false
      this.currentRecord = record
      this.editForm = {
        status: record.status == null ? 0 : record.status,
        customer: record.customer || '',
        customerContact: record.customerContact || '',
        customerRemark: record.customerRemark || '',
        faultReason: ''
      }
      this.modalVisible = true
    },
    openRecordModal(record) {
      this.recordDevice = record
      this.recordVisible = true
      this.recordPagination.current = 1
      this.loadRecordData()
    },
    handleRecordTableChange(pagination) {
      this.recordPagination.current = pagination.current
      this.recordPagination.pageSize = pagination.pageSize
      this.loadRecordData()
    },
    loadRecordData() {
      if (!this.recordDevice) return
      this.recordLoading = true
      findStatusRecordPage({
        objectid: this.recordDevice.objectid,
        page: this.recordPagination.current,
        pageSize: this.recordPagination.pageSize
      })
        .then(res => {
          const data = (res && res.data) || {}
          this.recordList = data.rows || []
          this.recordPagination.total = data.total || 0
        })
        .catch(() => {
          this.$message.error('记录查询失败')
        })
        .finally(() => {
          this.recordLoading = false
        })
    },
    submitStatus() {
      if (this.editForm.status === 4 && !(this.editForm.customer || '').trim()) {
        this.$message.warning('出厂请填写所属客户')
        return
      }
      if (this.editForm.status === 3 && !(this.editForm.faultReason || '').trim()) {
        this.$message.warning('故障请填写故障原因')
        return
      }
      if (this.editForm.status === 5 && !(this.editForm.customerRemark || '').trim()) {
        this.$message.warning('异常请填写异常信息')
        return
      }
      const payload = {
        status: this.editForm.status,
        customer: (this.editForm.customer || '').trim(),
        customerContact: (this.editForm.customerContact || '').trim(),
        customerRemark: (this.editForm.customerRemark || '').trim(),
        faultReason: (this.editForm.faultReason || '').trim()
      }
      this.saving = true
      const req = this.batchMode
        ? batchUpdateMaintainStatus({
          ...payload,
          objectidList: this.selectedRowKeys
        })
        : updateMaintainStatus({
          ...payload,
          objectid: this.currentRecord.objectid
        })
      req.then(res => {
        if (res.code === 0 || res.code === 200) {
          const msg = this.batchMode
            ? (`批量更新成功：${res.data || this.selectedRowKeys.length} 台`)
            : '状态已更新'
          this.$message.success(msg)
          this.modalVisible = false
          this.selectedRowKeys = []
          this.selectedRows = []
          this.loadData()
        } else {
          this.$message.error(res.message || '更新失败')
        }
      }).catch(() => {
        this.$message.error('更新失败')
      }).finally(() => {
        this.saving = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.maintain-page {
  .page-hd {
    margin-bottom: 12px;
  }
  .page-title {
    font-size: 22px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.3;
  }
  .tip {
    margin-top: 6px;
    color: #8c8c8c;
    font-size: 12px;
    line-height: 1.5;
  }
  .search-card,
  .table-card {
    margin-bottom: 12px;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }
  .toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }
  .toolbar-left,
  .toolbar-right {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }
  .w-status {
    width: 140px;
  }
  .w-keyword {
    width: 280px;
  }
  .batch-toggle {
    margin-left: 4px;
    color: #5487D2;
    font-size: 13px;
    user-select: none;
  }
  .batch-panel {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid #f0f0f0;
  }
  .field-label {
    margin-bottom: 6px;
    color: #595959;
    font-size: 13px;
  }
  .search-btn {
    color: #fff;
    background-color: rgba(232, 77, 17, 0.85);
    border-color: rgba(232, 77, 17, 0.85);
  }
  .cell-full {
    display: inline-block;
    white-space: nowrap;
    font-size: 12px;
    line-height: 1.4;
  }
  .cell-ellipsis {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
    font-size: 12px;
  }
  .pos-text {
    max-width: 248px;
  }
  .op-link + .op-link {
    margin-left: 10px;
  }
}
</style>

<style lang="scss">
.maintain-page .maintain-table {
  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    padding: 8px 8px !important;
  }
}
</style>
